import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { BsCustomDates } from 'ngx-bootstrap/datepicker/themes/bs/bs-custom-dates-view.component';
import { DateService } from '../../../table-filters/services/date.service';
import { RRIInput } from '../../interfaces/input-settings.interface';

@Component({
  selector: 'rr-date-range-field',
  templateUrl: './date-range-field.component.html',
  styleUrls: ['./date-range-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateRangeFieldComponent),
      multi: true,
    },
  ],
})
export class DateRangeFieldComponent
  implements OnInit, ControlValueAccessor, Validator
{
  public bsConfig: Partial<BsDatepickerConfig>;
  public data: Date[];
  public onChangeFn: any = (_: any) => {};
  public onTouchFn: any = () => {};
  public control: NgControl;
  public iconClassName = 'info-icon-two-action-buttons';
  private errorMessages = new Map<string, () => string>();
  private _dateRanges: BsCustomDates[];

  @Input() settings: RRIInput<Date[]>;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() bsSettings?: Partial<BsDatepickerConfig>;
  @Input() customErrorMessages: Map<string, () => string>;
  @Output() clearInput: EventEmitter<boolean> = new EventEmitter();

  constructor(private inj: Injector, private _dateService: DateService) {
    this.errorMessages.set('required', () => `Required`);
    const today = this._dateService.getToday();
    const yesterday = this._dateService.getYesterday();
    const thisWeek = this._dateService.getThisWeek();
    const lastWeek = this._dateService.getLastWeek();
    const thisMonth = this._dateService.getThisMonth();
    const lastMonth = this._dateService.getLastMonth();
    this._dateRanges = [
      {
        value: [
          new Date(today.setHours(0, 0, 0, 0)),
          new Date(today.setHours(23, 59, 0, 0)),
        ],
        label: 'Today',
      },
      {
        value: [
          new Date(yesterday.setHours(0, 0, 0, 0)),
          new Date(yesterday.setHours(23, 59, 0, 0)),
        ],
        label: 'Yesterday',
      },
      {
        value: thisWeek,
        label: 'This Week',
      },
      {
        value: lastWeek,
        label: 'Last Week',
      },
      {
        value: thisMonth,
        label: 'This Month',
      },
      {
        value: lastMonth,
        label: 'Last Month',
      },
    ];
    this.bsConfig = {
      containerClass: 'theme-default',
      isAnimated: true,
      dateInputFormat: 'DD/MM/YYYY',
    };
  }

  ngOnInit(): void {
    if (this.settings?.hideResetInput) {
      this.iconClassName = 'info-icon-no-clear-two-action-buttons';
    }
    this.control = this.inj.get(NgControl, null);

    if (this.customErrorMessages && this.customErrorMessages.size) {
      this.errorMessages = new Map([
        ...Array.from(this.errorMessages.entries()),
        ...Array.from(this.customErrorMessages.entries()),
      ]);
    }
    if (this.settings && this.settings.showDateRanges) {
      this.bsConfig.ranges = this._dateRanges;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.settings.disableClearEvent) {
      if (this.settings.defaultValue) {
        this.data = this.settings.defaultValue;
      } else {
        this.data = null;
      }
    }
    this.clearInput.emit(true);
    this.onChange();
  }

  public onClearInput(): void {
    if (!this.settings.disableClearEvent) {
      if (this.settings.defaultValue) {
        this.data = this.settings.defaultValue;
      } else {
        this.data = null;
      }
    }
    this.clearInput.emit(true);
    this.onChange();
  }

  public onValueChange(event: Date[]): void {
    if (
      event &&
      Array.isArray(event) &&
      event.length &&
      (event.length != 2 || (event.length == 2 && event.some((x) => x == null)))
    ) {
      this.data = null;
      this.onChange();
    } else if (this.data != event) {
      this.data = event;
      this.onChange();
    }
  }

  public writeValue(value: number[]): void {
    if (value && value.length === 2) {
      this.data = [new Date(value[0] * 1000), new Date(value[1] * 1000)];
    } else {
      this.data = null;
    }
  }

  public registerOnChange(fn: Date[]): void {
    this.onChangeFn = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchFn = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  public onChange() {
    this.onChangeFn(this.data);
  }

  public get invalid(): boolean {
    return this.control ? this.control.invalid : false;
  }

  public get showError(): boolean {
    if (!this.control) {
      return false;
    }

    const { dirty, touched, value } = this.control;

    return this.invalid ? dirty || touched || !!value : false;
  }

  public get errors(): Array<string> {
    if (!this.control) {
      return [];
    }

    const { errors } = this.control;
    return Object.keys(errors).map((key) => {
      return this.errorMessages.has(key)
        ? this.errorMessages.get(key)()
        : 'error: ' + key;
    });
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    return null;
  }
}
